import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Modal, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import distributorService from '../../../../services/DistributorService';

const PaymentGatewayConfig = () => {
  const [configurations, setConfigurations] = useState([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [configData, setConfigData] = useState({
    operator: '',
    status: 'active',
    cookie: '',
    csrf_token: '',
    token: '',
    headers: {},
    payload: {}
  });

  // Ref to prevent duplicate calls in StrictMode
  const hasInitialized = useRef(false);

  // Fetch UPI configs on component mount
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      fetchUPIConfigs();
    }
  }, []);

  const fetchUPIConfigs = async () => {
    setLoading(true);
    try {
      const response = await distributorService.getUPIConfigs();
      if (response.success && response.data) {
        setConfigurations(response.data.results || []);
      } else {
        toast.error(response.error || 'Failed to fetch UPI configurations');
      }
    } catch (error) {
      console.error('Error fetching UPI configs:', error);
      toast.error('Failed to fetch UPI configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedConfig(null);
    setModalMode('add');
    setConfigData({
      operator: '',
      status: 'active',
      cookie: '',
      csrf_token: '',
      token: '',
      headers: {},
      payload: {}
    });
    setShowConfigModal(true);
  };

  const handleEdit = (config) => {
    setSelectedConfig(config);
    setModalMode('edit');
    setConfigData({
      operator: config.operator,
      status: config.status,
      cookie: config.cookie || '',
      csrf_token: config.csrf_token || '',
      token: config.token || '',
      headers: config.headers || {},
      payload: config.payload || {}
    });
    setShowConfigModal(true);
  };

  const handleDelete = async (config) => {
    if (window.confirm(`Are you sure you want to delete ${config.operator} configuration?`)) {
      try {
        const response = await distributorService.deleteUPIConfig(config.id);
        if (response.success) {
          toast.success(response.message || 'UPI configuration deleted successfully');
          fetchUPIConfigs(); // Refresh the list
        } else {
          toast.error(response.error || 'Failed to delete UPI configuration');
        }
      } catch (error) {
        console.error('Error deleting UPI config:', error);
        toast.error('Failed to delete UPI configuration');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Prepare data with proper formatting
      const submitData = {
        ...configData,
        headers: typeof configData.headers === 'string' ? 
          (configData.headers ? JSON.parse(configData.headers) : {}) : configData.headers,
        payload: typeof configData.payload === 'string' ? 
          (configData.payload ? JSON.parse(configData.payload) : {}) : configData.payload
      };

      if (modalMode === 'add') {
        const response = await distributorService.createUPIConfig(submitData);
        if (response.success) {
          toast.success(response.message || 'UPI configuration added successfully');
          fetchUPIConfigs();
          setShowConfigModal(false);
        } else {
          toast.error(response.error || 'Failed to add UPI configuration');
        }
      } else {
        const response = await distributorService.updateUPIConfig(selectedConfig.id, submitData);
        if (response.success) {
          toast.success(response.message || 'UPI configuration updated successfully');
          fetchUPIConfigs();
          setShowConfigModal(false);
        } else {
          toast.error(response.error || 'Failed to update UPI configuration');
        }
      }
    } catch (error) {
      console.error('Error submitting UPI config:', error);
      toast.error('Failed to save UPI configuration');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfigData(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const getStatusBadge = (status) => {
    return status === 'active' ? 
      <Badge bg="success">Active</Badge> : 
      <Badge bg="danger">Inactive</Badge>;
  };


  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-mobile-alt me-2"></i>
          UPI Configuration
        </h5>
        <Button variant="primary" onClick={handleAdd}>
          <i className="fas fa-plus me-2"></i>
          Add UPI Config
        </Button>
      </div>


      {/* Configurations Table */}
      <h5 className="mb-3" style={{ color: '#031a6f' }}>
        <i className="fas fa-table me-2"></i>
        UPI Configurations ({configurations.length} configs)
      </h5>
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2">Loading UPI configurations...</div>
        </div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover className="modern-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>UPI Provider</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Updated At</th>
              </tr>
            </thead>
            <tbody>
              {configurations.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center">
                    No UPI configurations found
                  </td>
                </tr>
              ) : (
                configurations.map((config) => (
                  <tr key={config.id}>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEdit(config)}
                          title="Edit Configuration"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(config)}
                          title="Delete Configuration"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong className="text-capitalize">{config.operator}</strong>
                        <br />
                        <small className="text-muted">ID: {config.id}</small>
                      </div>
                    </td>
                    <td>{getStatusBadge(config.status)}</td>
                    <td>
                      <small>{new Date(config.created_at).toLocaleDateString()}</small>
                    </td>
                    <td>
                      <small>{new Date(config.updated_at).toLocaleDateString()}</small>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}

      {/* Configuration Form Modal */}
      <Modal show={showConfigModal} onHide={() => setShowConfigModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'add' ? 'Add New' : 'Edit'} Payment Gateway
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Alert variant="warning">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Sensitive information like API keys and secrets will be encrypted and stored securely.
            </Alert>
            
            <Row>
              {modalMode === 'add' && (
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Operator *</Form.Label>
                    <Form.Select
                      value={configData.operator}
                      onChange={(e) => handleInputChange('operator', e.target.value)}
                      required
                    >
                      <option value="">Select Provider</option>
                      <option value="tezgateway">tezgateway</option>
                      <option value="Paytm">Paytm</option>
                      <option value="PhonePe">PhonePe</option>
                      
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Status *</Form.Label>
                  <Form.Select
                    value={configData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Token *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={configData.token}
                    onChange={(e) => handleInputChange('token', e.target.value)}
                    placeholder="Enter authentication token"
                    required
                  />
                  <Form.Text className="text-muted">
                    Enter the API token or authentication key for the payment gateway
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfigModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {modalMode === 'add' ? 'Add' : 'Update'} Gateway
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

    </Container>
  );
};

export default PaymentGatewayConfig;